<script>
// EditAgents.vue
import { mapGetters } from 'vuex';
import router from '../../../../index';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { toast } from 'dashboard/helper/toast';

import Spinner from 'shared/components/Spinner.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import AgentSelector from '../AgentSelector.vue';
import Icon from 'next/icon/Icon.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import { buildAgentTeamsIndex } from '../agentTeams';

export default {
  components: {
    Spinner,
    PageHeader,
    AgentSelector,
    Icon,
    TagMultiSelectComboBox,
  },
  validations: {
    selectedAgents: {
      isEmpty() {
        return !!this.selectedAgents.length;
      },
    },
    uniformInboxes: {
      isEmpty() {
        return !!this.uniformInboxes.length;
      },
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      selectedAgents: [],
      isCreating: false,
      uniformInboxes: [],
      // { [userId]: [{ id, name, inboxIds }] } — outros times de que o agente
      // já participa. Serve só para AVISAR na tabela; nunca bloqueia.
      agentTeamsByUser: {},
      isLoadingTeams: false,
      isInitialLoading: true, // Nova flag
    };
  },

  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      uiFlags: 'teamMembers/getUIFlags',
      allTeams: 'teams/getTeams',
    }),

    teamId() {
      return this.$route.params.teamId;
    },
    headerTitle() {
      return this.$t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.TITLE', {
        teamName: this.currentTeam.name,
      });
    },
    currentTeam() {
      return this.$store.getters['teams/getTeam'](this.teamId);
    },
    teamMembers() {
      return this.$store.getters['teamMembers/getTeamMembers'](this.teamId);
    },
    showAgentsList() {
      const { id } = this.currentTeam;
      // Só mostra quando:
      // 1. Carregamento inicial terminou
      // 2. Time existe
      // 3. Não está fetchando os membros
      // 4. Os outros times já foram carregados (para montar os avisos)
      return (
        !this.isInitialLoading &&
        id &&
        !this.uiFlags.isFetching &&
        !this.isLoadingTeams
      );
    },
    filteredInboxes() {
      const inboxes = this.$store.getters['inboxes/getInboxes'];
      return inboxes.filter(inbox => inbox.channel_type !== 'Channel::Voice');
    },
    inboxOptions() {
      return this.filteredInboxes.map(inbox => ({
        value: inbox.id,
        label: inbox.name,
      }));
    },
    enrichedAgentList() {
      return this.agentList.map(agent => {
        // agent.id é o user_id
        // member.id no teamMembers também representa o user_id
        const isInCurrentTeam = this.teamMembers.some(tm => tm.id === agent.id);
        const otherTeams = this.agentTeamsByUser[agent.id] || [];

        // Sobreposição = o agente já atende, em outro time, uma das inboxes
        // que estamos distribuindo aqui. Ele vai entrar no round-robin dos
        // dois times. É informação, não impedimento.
        const overlappingTeams = otherTeams.filter(team =>
          team.inboxIds.some(inboxId => this.uniformInboxes.includes(inboxId))
        );

        return {
          ...agent,
          isInCurrentTeam,
          otherTeamNames: otherTeams.map(team => team.name),
          overlappingTeams: overlappingTeams.map(team => team.name),
        };
      });
    },
  },

  watch: {
    async teamId(newTeamId, oldTeamId) {
      if (newTeamId !== oldTeamId) {
        // Reseta o estado
        this.isInitialLoading = true;
        this.selectedAgents = [];
        this.uniformInboxes = [];
        this.agentTeamsByUser = {};
        this.isLoadingTeams = true;

        // Recarrega os dados do novo time
        await this.loadTeamData();
      }
    },
  },

  async mounted() {
    await this.loadTeamData();
  },

  methods: {
    async loadTeamData() {
      const { teamId } = this.$route.params;

      this.isInitialLoading = true;

      // Carrega inboxes, agents e teams em paralelo
      await Promise.all([
        this.$store.dispatch('inboxes/get'),
        this.$store.dispatch('agents/get'),
        this.$store.dispatch('teams/get'),
      ]);

      try {
        // IMPORTANTE: Sempre recarrega os membros do time atual (sem cache)
        await this.$store.dispatch('teamMembers/get', { teamId });

        // Carrega os outros times (para os avisos de sobreposição)
        await this.loadAgentTeams();

        // Detectar inboxes comuns a todos os membros
        const inboxMap = {};
        this.teamMembers.forEach(member => {
          if (member.inbox_id && member.inbox) {
            if (!inboxMap[member.inbox.id]) {
              inboxMap[member.inbox.id] = new Set();
            }
            // member.id representa o user_id
            inboxMap[member.inbox.id].add(member.id);
          }
        });

        // member.id representa o user_id dos agentes únicos no time atual
        const uniqueAgentIds = [
          ...new Set(this.teamMembers.map(item => item.id)),
        ];
        this.updateSelectedAgents(uniqueAgentIds);

        // Detectar inboxes que são comuns a todos os agentes
        const commonInboxIds = Object.entries(inboxMap)
          .filter(([, agentSet]) => agentSet.size === uniqueAgentIds.length)
          .map(([inboxId]) => parseInt(inboxId, 10));

        this.uniformInboxes = commonInboxIds;
      } catch (error) {
        // Sem os membros carregados a tela mostraria seleção vazia como se o
        // time não tivesse ninguém — avisa em vez de fingir que está tudo bem.
        this.updateSelectedAgents([]);
        toast.error(
          error.message ||
            'Erro ao carregar os agentes do time. Recarregue a página.'
        );
      } finally {
        this.isInitialLoading = false;
      }
    },

    async loadAgentTeams() {
      this.isLoadingTeams = true;
      const currentTeamId = parseInt(this.teamId, 10);

      try {
        const otherTeams = this.allTeams.filter(
          team => team.id !== currentTeamId
        );

        // Carrega todos os times em paralelo - SEMPRE recarrega para pegar dados atualizados
        const results = await Promise.all(
          otherTeams.map(async team => {
            await this.$store.dispatch('teamMembers/get', { teamId: team.id });
            const members = this.$store.getters['teamMembers/getTeamMembers'](
              team.id
            );
            return { team, members: members || [] };
          })
        );

        this.agentTeamsByUser = buildAgentTeamsIndex(results);
      } finally {
        this.isLoadingTeams = false;
      }
    },

    updateSelectedAgents(newAgentList) {
      this.v$.selectedAgents.$touch();
      this.selectedAgents = [...newAgentList];
    },

    async addAgents() {
      // Valida campos obrigatórios
      this.v$.selectedAgents.$touch();
      this.v$.uniformInboxes.$touch();

      // Verifica se há erros de validação
      if (this.v$.$invalid) {
        if (this.v$.uniformInboxes.$invalid) {
          useAlert(
            'Por favor, selecione ao menos uma inbox para distribuir aos agentes.'
          );
        }
        if (this.v$.selectedAgents.$invalid) {
          useAlert('Por favor, selecione ao menos um agente.');
        }
        return;
      }

      this.isCreating = true;
      const { teamId, selectedAgents } = this;

      try {
        const agentInboxMapping = this.getUniformMapping();

        await this.$store.dispatch('teamMembers/update', {
          teamId,
          agentsList: selectedAgents,
          agentInboxMapping,
        });

        router.replace({
          name: 'settings_teams_edit_finish',
          params: { page: 'edit', teamId },
        });
        this.$store.dispatch('teams/get');
      } catch (error) {
        console.error('Erro ao adicionar agentes:', error);
        useAlert(error.message || 'Erro ao atualizar agentes do time');
      } finally {
        this.isCreating = false;
      }
    },

    getUniformMapping() {
      const mapping = {};
      this.selectedAgents.forEach(agentId => {
        mapping[agentId] = this.uniformInboxes.map(inboxId => {
          const inbox = this.filteredInboxes.find(i => i.id === inboxId);
          return {
            id: inbox.id,
            name: inbox.name,
          };
        });
      });
      return mapping;
    },
  },
};
</script>

<template>
  <div
    class="border border-n-weak bg-n-solid-1 rounded-t-lg border-b-0 h-full w-full p-6 col-span-6 overflow-auto"
  >
    <form
      class="flex flex-wrap mx-0 overflow-x-auto"
      @submit.prevent="addAgents"
    >
      <div class="w-full">
        <PageHeader
          :header-title="headerTitle"
          :header-content="$t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.DESC')"
        />
      </div>

      <!-- Seleção de Inboxes -->
      <div class="w-full mb-6">
        <div class="bg-n-alpha-2 rounded-lg p-4">
          <div class="flex items-center gap-2 mb-3">
            <Icon icon="i-lucide-inbox" class="text-n-slate-11" />
            <h4 class="text-base font-semibold text-n-slate-12">
              Distribuição de Inboxes
            </h4>
          </div>

          <p class="text-sm text-n-slate-11 mb-4">
            Selecione as inboxes que serão distribuídas para todos os agentes do
            time
          </p>

          <TagMultiSelectComboBox
            v-model="uniformInboxes"
            :options="inboxOptions"
            placeholder="Selecione as inboxes"
            search-placeholder="Buscar inboxes..."
            empty-state="Nenhuma inbox encontrada"
          />

          <!-- Mensagem de erro -->
          <div v-if="v$.uniformInboxes.$error" class="mt-2">
            <p class="text-sm text-n-red-11 font-medium">
              ⚠️ Selecione ao menos uma inbox
            </p>
          </div>

          <!-- Feedback positivo -->
          <div
            v-else-if="uniformInboxes.length > 0 && selectedAgents.length > 0"
            class="mt-3 p-3 bg-n-blue-alpha-3 rounded-md border border-n-blue-9"
          >
            <div class="flex items-center gap-2 text-sm text-n-blue-11">
              <Icon icon="i-lucide-info" class="flex-shrink-0" />
              <span>
                <strong>{{ uniformInboxes.length }}</strong> inbox(es) serão
                distribuídas para
                <strong>{{ selectedAgents.length }}</strong> agente(s)
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Seleção de Agentes -->
      <div class="w-full">
        <div v-if="v$.selectedAgents.$error">
          <p class="error-message pb-2">
            {{ $t('TEAMS_SETTINGS.ADD.AGENT_VALIDATION_ERROR') }}
          </p>
        </div>
        <AgentSelector
          v-if="showAgentsList"
          :agent-list="enrichedAgentList"
          :selected-agents="selectedAgents"
          :update-selected-agents="updateSelectedAgents"
          :is-working="isCreating"
          :submit-button-text="
            $t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.BUTTON_TEXT')
          "
          :is-loading="false"
          :has-inboxes="uniformInboxes.length > 0"
        />
        <AgentSelector
          v-else
          :agent-list="[]"
          :selected-agents="[]"
          :update-selected-agents="() => {}"
          :is-working="false"
          :submit-button-text="
            $t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.BUTTON_TEXT')
          "
          is-loading
          :has-inboxes="false"
        />
      </div>
    </form>
  </div>
</template>
